import React, { useState } from 'react';
import { UserPlus, Send, CheckCircle, Loader2, Heart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const VisitorRegistration = () => {
  const { lang } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const texts = {
    de: {
      title: 'Zum ersten Mal bei uns?',
      subtitle: 'Melde dich an, damit wir dich persoenlich begrüssen koennen!',
      name: 'Dein Name',
      email: 'Deine E-Mail',
      message: 'Moechtest du uns etwas mitteilen? (optional)',
      send: 'Anmelden',
      sending: 'Wird gesendet...',
      success: 'Willkommen! Wir freuen uns auf deinen Besuch.',
      successSub: 'Du erhaeltst bald eine Bestaetigung per E-Mail.',
    },
    en: {
      title: 'Visiting for the first time?',
      subtitle: 'Register so we can greet you personally!',
      name: 'Your name',
      email: 'Your email',
      message: 'Would you like to tell us something? (optional)',
      send: 'Register',
      sending: 'Sending...',
      success: 'Welcome! We look forward to your visit.',
      successSub: 'You will receive a confirmation email shortly.',
    },
    fr: {
      title: 'Premiere visite chez nous?',
      subtitle: 'Inscrivez-vous pour que nous puissions vous accueillir personnellement!',
      name: 'Votre nom',
      email: 'Votre e-mail',
      message: 'Souhaitez-vous nous dire quelque chose? (facultatif)',
      send: 'S\'inscrire',
      sending: 'Envoi en cours...',
      success: 'Bienvenue! Nous nous rejouissons de votre visite.',
      successSub: 'Vous recevrez bientot un e-mail de confirmation.',
    },
  };
  const t = texts[lang] || texts.de;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/contact/visitor-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed');
      setSubmitted(true);
    } catch {
      setError('Fehler. Bitte versuche es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-[#0f2440]" id="register">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-sky-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <Heart className="w-8 h-8 text-sky-300" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              {t.title}
            </h2>
            <p className="text-base text-white/70 max-w-lg mx-auto">
              {t.subtitle}
            </p>
          </div>

          {submitted ? (
            <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-8 text-center" data-testid="visitor-success">
              <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-4" />
              <p className="text-white text-xl font-bold mb-2">{t.success}</p>
              <p className="text-white/70">{t.successSub}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="visitor-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder={t.name}
                    data-testid="visitor-name"
                    className="w-full px-5 py-3.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder={t.email}
                    data-testid="visitor-email"
                    className="w-full px-5 py-3.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <textarea
                  rows={2}
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  placeholder={t.message}
                  data-testid="visitor-message"
                  className="w-full px-5 py-3.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-sky-400 focus:border-transparent resize-none"
                />
              </div>
              {error && <p className="text-red-300 text-sm text-center">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                data-testid="visitor-submit"
                className="w-full px-6 py-4 bg-white text-[#0f2440] rounded-xl font-bold text-lg hover:bg-sky-50 transition-all hover:scale-[1.02] hover:shadow-xl flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 animate-spin" />{t.sending}</>
                ) : (
                  <><UserPlus className="w-5 h-5" />{t.send}</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default VisitorRegistration;
